/* MIRA Y MANO — AW26 motion layer. No dependencies; Lenis (vendored) is loaded on demand.
   Settings arrive as body[data-mo-*]; see config/settings_schema.json → Motion. */
(() => {
  const root = document.documentElement;
  const body = document.body;
  if (!body || window.mymMotion) return;
  const ds = body.dataset;
  const on = (key) => ds[key] === 'true';
  const mq = (q) => window.matchMedia(q).matches;
  const reduce = mq('(prefers-reduced-motion: reduce)');
  const finePointer = mq('(hover: hover) and (pointer: fine)');
  const designMode = !!(window.Shopify && window.Shopify.designMode) || root.classList.contains('shopify-design-mode');
  const K = { subtle: 0.6, balanced: 1, expressive: 1.4 }[ds.moIntensity] || 1;
  const strings = (window.theme && window.theme.strings) || {};
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
  const hasIO = 'IntersectionObserver' in window;

  /* Hero video: fade in once playing, pause off-screen, never autoplay for reduced motion */
  const initMedia = (scope = document) => {
    scope.querySelectorAll('[data-mo-video]').forEach((video) => {
      if (video.dataset.moBound) return;
      video.dataset.moBound = '1';
      if (reduce) { video.pause(); video.removeAttribute('autoplay'); return; }
      const shown = () => video.classList.add('is-playing');
      if (!video.paused && video.readyState > 2) shown(); else video.addEventListener('playing', shown, { once: true });
      const p = video.play && video.play();
      if (p && p.catch) p.catch(() => {});
      if (hasIO) new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) { video.pause(); return; }
        if (video.dataset.userPaused) return;
        const q = video.play();
        if (q && q.catch) q.catch(() => {});
      }).observe(video);
    });
    scope.querySelectorAll('[data-mo-video-toggle]').forEach((btn) => {
      if (btn.dataset.moBound) return;
      btn.dataset.moBound = '1';
      const hero = btn.closest('.hero');
      const video = hero && hero.querySelector('[data-mo-video]');
      const frame = hero && hero.querySelector('[data-mo-embed] iframe');
      btn.addEventListener('click', () => {
        const paused = !btn.classList.contains('is-paused');
        btn.classList.toggle('is-paused', paused);
        btn.setAttribute('aria-pressed', String(paused));
        btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
        if (video) { video.dataset.userPaused = paused ? '1' : ''; if (paused) video.pause(); else video.play().catch(() => {}); }
        if (frame) {
          const wrap = frame.parentElement;
          if (paused) { wrap.classList.remove('is-playing'); frame.dataset.src = frame.src || frame.dataset.src; frame.removeAttribute('src'); }
          else { frame.src = frame.dataset.src; setTimeout(() => wrap.classList.add('is-playing'), 900); }
        }
      });
    });
    scope.querySelectorAll('[data-mo-embed]').forEach((wrap) => {
      if (wrap.dataset.moBound || reduce) return;
      wrap.dataset.moBound = '1';
      const frame = wrap.querySelector('iframe[data-src]');
      if (!frame) return;
      const load = () => idle(() => {
        frame.addEventListener('load', () => setTimeout(() => wrap.classList.add('is-playing'), 900), { once: true });
        frame.src = frame.dataset.src;
      });
      if (document.readyState === 'complete') load(); else window.addEventListener('load', load, { once: true });
    });
  };


  /* Hero slideshow: slide 01 is the section image. The active segment's progress line is the timer
     (its CSS animation ends → next slide), so pausing the line pauses the slideshow exactly.
     Without the motion layer the segments still switch slides, instantly. */
  const initSlideshows = (scope = document, animated = false) => {
    scope.querySelectorAll('[data-slideshow]').forEach((hero) => {
      if (hero.dataset.moSlides) return;
      const slides = [...hero.querySelectorAll('[data-slide]')];
      if (slides.length < 2) return;
      hero.dataset.moSlides = '1';
      const caps = [...hero.querySelectorAll('[data-slide-caption]')];
      const segs = [...hero.querySelectorAll('[data-slide-to]')];
      const current = hero.querySelector('[data-slide-current]');
      const toggle = hero.querySelector('[data-slide-toggle]');
      const fx = ds.moSlideFx || 'fade';
      const leaveMs = animated ? { fade: 1700, dip: 1700 }[fx] || 0 : 0;
      const autoplay = animated && on('moSlideshow') && !designMode;
      let i = 0;
      let leaveT = 0;
      // phones-only slideshows ([data-slideshow-mobile]) never move on desktop; the next slide is put in the page ahead of its turn
      const phonesOnly = hero.hasAttribute('data-slideshow-mobile');
      const allowed = () => !phonesOnly || mq('(max-width: 749px)');
      const warm = (k) => { const s = slides[(k + slides.length) % slides.length]; s.classList.add('is-warm'); s.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }); };
      if (phonesOnly) window.addEventListener('load', () => idle(() => { if (allowed()) warm(1); }), { once: true });
      if (autoplay) hero.classList.add('is-auto');
      const go = (n) => {
        if (!allowed()) return;
        n = (n + slides.length) % slides.length;
        if (n === i) return;
        const prev = slides[i];
        slides.forEach((s) => s.classList.remove('is-leaving'));
        prev.classList.remove('is-active');
        if (leaveMs) prev.classList.add('is-leaving');
        slides[n].classList.add('is-active');
        slides.forEach((s, k) => s.setAttribute('aria-hidden', String(k !== n)));
        caps.forEach((c, k) => c.classList.toggle('is-active', k === n));
        segs.forEach((b, k) => {
          b.classList.toggle('is-active', k === n);
          b.classList.toggle('is-done', k < n);
          if (k === n) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
        });
        if (current) current.textContent = String(n + 1).padStart(2, '0');
        clearTimeout(leaveT);
        if (leaveMs) leaveT = setTimeout(() => prev.classList.remove('is-leaving'), leaveMs);
        // warm the photograph that follows
        if (phonesOnly) warm(n + 1);
        slides[(n + 1) % slides.length].querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        i = n;
      };
      segs.forEach((b, k) => b.addEventListener('click', () => go(k)));
      hero.addEventListener('animationend', (e) => {
        if (e.animationName === 'mo-progress' && autoplay) go(i + 1);
      });
      if (toggle) toggle.addEventListener('click', () => {
        const paused = hero.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      });
      // keyboard users reading the hero keep the current slide
      hero.addEventListener('focusin', (e) => {
        let kb = true;
        try { kb = e.target.matches(':focus-visible'); } catch (err) { /* older browsers: hold */ }
        if (kb) hero.classList.add('is-held');
      });
      hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) hero.classList.remove('is-held'); });
      if (hasIO) new IntersectionObserver(([e]) => hero.classList.toggle('is-offscreen', !e.isIntersecting)).observe(hero);
      document.addEventListener('visibilitychange', () => hero.classList.toggle('is-tab-hidden', document.hidden));
      // touch swipe; mirrored in Arabic
      let sx = null;
      let sy = 0;
      hero.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') { sx = e.clientX; sy = e.clientY; hero.classList.add('is-touching'); } }, { passive: true });
      ['pointercancel', 'pointerleave'].forEach((t) => hero.addEventListener(t, () => { hero.classList.remove('is-touching'); sx = null; }, { passive: true }));
      hero.addEventListener('pointerup', (e) => {
        hero.classList.remove('is-touching');
        if (sx === null) return;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        sx = null;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        const rtl = getComputedStyle(hero).direction === 'rtl';
        go(i + ((dx < 0) !== rtl ? 1 : -1));
      }, { passive: true });
      // theme editor: selecting a Slide block shows it and holds it
      document.addEventListener('shopify:block:select', (e) => {
        const k = slides.indexOf(e.target);
        if (k < 0) return;
        go(k);
        hero.classList.add('is-held');
      });
      document.addEventListener('shopify:block:deselect', (e) => { if (slides.includes(e.target)) hero.classList.remove('is-held'); });
    });
  };

  if (!on('motion') || reduce || window.mymMotionTimedOut) {
    root.classList.remove('mo');
    const refreshStatic = (scope) => { initMedia(scope); initSlideshows(scope); };
    window.mymMotion = { refresh: refreshStatic };
    refreshStatic();
    document.addEventListener('shopify:section:load', (e) => refreshStatic(e.target));
    return;
  }
  root.classList.add('mo', 'mo-ready');

  /* Wrap every word of an element's text nodes (words, never letters: safe for Arabic joining) */
  const eachWord = (el, make) => {
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          if (!child.textContent.trim()) return;
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            frag.appendChild(part.trim() ? make(part, i++) : document.createTextNode(part));
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') walk(child);
      });
    };
    walk(el);
  };

  /* Headline: each word rises behind a mask */
  const splitWords = (el) => {
    if (el.classList.contains('mo-split')) return;
    // word masks are atomic inlines, so a Latin run inside an Arabic line (or the reverse) would be
    // re-ordered by the bidi algorithm; such mixed headings keep the plain line fade instead
    const rtl = getComputedStyle(el).direction === 'rtl';
    if (rtl ? /[A-Za-zÀ-ɏ]/.test(el.textContent) : /[֐-ࣿיִ-﷿ﹰ-﻿]/.test(el.textContent)) return;
    eachWord(el, (word, i) => {
      const outer = document.createElement('span');
      const inner = document.createElement('span');
      outer.className = 'mo-word';
      inner.className = 'mo-word__inner';
      inner.style.setProperty('--w', i);
      inner.textContent = word;
      outer.appendChild(inner);
      return outer;
    });
    el.classList.add('mo-split');
  };

  /* Kinetic tagline: words appear one by one at a caption cadence. Plain inline spans and opacity
     only, so mixed Arabic/Latin lines keep their natural bidi order. */
  const splitKinetic = (el) => {
    if (el.classList.contains('mo-kinetic')) return;
    eachWord(el, (word, i) => {
      const w = document.createElement('span');
      w.className = 'mo-kw';
      w.style.setProperty('--w', i);
      w.textContent = word;
      return w;
    });
    // siblings that follow the tagline (text, link) wait for its last word
    const n = el.querySelectorAll('.mo-kw').length;
    [el, el.parentElement].forEach((x) => x && x.style.setProperty('--mo-kw-n', n));
    el.classList.add('mo-kinetic');
  };

  /* Scroll reveals: one observer, entries arriving together are staggered */
  const revealIO = hasIO && new IntersectionObserver((entries) => {
    let n = 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.style.setProperty('--mo-delay', `${Math.min(n++, 6) * 0.09}s`);
      el.classList.add('is-in');
      revealIO.unobserve(el);
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  const initReveal = (scope = document) => {
    if (on('moText')) scope.querySelectorAll('[data-mo-split]').forEach(splitWords);
    if (on('moKinetic')) scope.querySelectorAll('[data-kinetic]').forEach(splitKinetic);
    const selectors = ['[data-mo-film]'];
    if (on('moReveal')) selectors.push('.reveal');
    if (on('moClip')) selectors.push('.mo-clip');
    if (on('moText')) selectors.push('.mo-split:not(.mo-hero-item)');
    if (on('moKinetic')) selectors.push('.mo-kinetic');
    if (on('moHud')) selectors.push('.mo-hud');
    scope.querySelectorAll(selectors.join(',')).forEach((el) => {
      if (el.closest('.mo-hero') || el.classList.contains('is-in')) return;
      if (revealIO && !designMode) revealIO.observe(el); else el.classList.add('is-in');
    });
  };

  /* Hero: wait for the photograph to decode (max 1.2s), then lift the curtain */
  const initHeroes = (scope = document) => {
    scope.querySelectorAll('[data-mo-hero]').forEach((hero) => {
      if (hero.dataset.moBound) return;
      hero.dataset.moBound = '1';
      if (on('moText')) hero.querySelectorAll('[data-mo-split]').forEach(splitWords);
      if (on('moKinetic')) hero.querySelectorAll('[data-kinetic]').forEach(splitKinetic);
      const play = () => requestAnimationFrame(() => hero.classList.add('is-in'));
      if (designMode) { play(); return; }
      if (hero.dataset.moHero === 'load') {
        const img = [...hero.querySelectorAll('.hero__media img')].find((i) => i.offsetParent !== null && !i.closest('.hero__slide:not(.is-active)'));
        const ready = img && !img.complete && img.decode ? img.decode().catch(() => {}) : Promise.resolve();
        Promise.race([ready, wait(1200)]).then(play);
      } else if (hasIO) {
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { play(); io.disconnect(); } }, { rootMargin: '0px 0px -15% 0px' });
        io.observe(hero);
      } else play();
    });
  };

  /* Parallax: transform-only, visible elements only, reads then writes in one frame */
  const parallax = new Set();
  const parallaxIO = hasIO && new IntersectionObserver((entries) => entries.forEach((e) => {
    if (e.isIntersecting) parallax.add(e.target); else parallax.delete(e.target);
  }), { rootMargin: '10% 0px' });
  const amp = (finePointer ? 0.06 : 0.025) * K;
  let pTick = false;
  const updateParallax = () => {
    pTick = false;
    const vh = window.innerHeight;
    const moves = [];
    parallax.forEach((el) => {
      const r = el.getBoundingClientRect();
      const p = clamp((r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2), -1, 1);
      moves.push([el.mymLayer, -p * r.height * amp * (Number(el.dataset.moSpeed) || 1)]);
    });
    moves.forEach(([layer, y]) => { if (layer) layer.style.translate = `0 ${y.toFixed(1)}px`; });
  };
  const requestParallax = () => { if (!pTick && parallax.size) { pTick = true; requestAnimationFrame(updateParallax); } };
  const initParallax = (scope = document) => {
    if (!on('moParallax') || !parallaxIO) return;
    scope.querySelectorAll('.mo-parallax').forEach((el) => {
      if (el.mymLayer) return;
      el.mymLayer = el.querySelector(':scope > img, :scope > svg');
      if (el.mymLayer) parallaxIO.observe(el);
    });
    requestParallax();
  };
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });

  /* Shimmer skeleton until each image has loaded; animates only while on screen */
  const shimmerIO = hasIO && new IntersectionObserver((entries) => entries.forEach((e) => {
    e.target.classList.toggle('is-loading', e.isIntersecting);
  }), { rootMargin: '200px 0px' });
  const initShimmer = (scope = document) => {
    if (!on('moShimmer')) return;
    scope.querySelectorAll('.mo-shimmer').forEach((box) => {
      if (box.dataset.moShimmer) return;
      box.dataset.moShimmer = '1';
      const img = box.querySelector('img:not(.card__image--second)');
      if (!img || img.complete) { box.classList.add('is-loaded'); return; }
      img.classList.add('mo-pending');
      const done = () => {
        img.classList.remove('mo-pending');
        img.classList.add('mo-fade');
        box.classList.add('is-loaded');
        box.classList.remove('is-loading');
        if (shimmerIO) shimmerIO.unobserve(box);
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      if (shimmerIO) shimmerIO.observe(box);
    });
  };

  /* Marquee: clone enough groups to cover the viewport, constant px/s speed, ease to a stop on hover */
  const initMarquees = (scope = document) => {
    scope.querySelectorAll('[data-mo-marquee]').forEach((el) => {
      if (el.dataset.moBound) return;
      el.dataset.moBound = '1';
      const track = el.querySelector('.marquee__track');
      const group = track && track.querySelector('.marquee__group');
      if (!group) return;
      const speed = (Number(ds.moMarqueeSpeed) || 40) * (Number(el.dataset.speedFactor) || 1);
      let lastW = 0;
      const measure = () => {
        const w = el.clientWidth;
        const gw = group.getBoundingClientRect().width;
        if (!gw || w === lastW) return;
        lastW = w;
        track.querySelectorAll('[data-clone]').forEach((c) => c.remove());
        const need = Math.ceil(w / gw) + 1;
        for (let i = track.children.length; i < need; i++) {
          const c = group.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('data-clone', '');
          c.querySelectorAll('a, button').forEach((a) => a.setAttribute('tabindex', '-1'));
          track.appendChild(c);
        }
        el.style.setProperty('--marquee-shift', `${gw}px`);
        el.style.setProperty('--marquee-duration', `${(gw / speed).toFixed(2)}s`);
      };
      (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(measure);
      let rt;
      window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(measure, 200); }, { passive: true });

      const anim = () => track.getAnimations && track.getAnimations()[0];
      let rateRaf = 0;
      const easeRate = (to) => {
        const a = anim();
        if (!a || !a.updatePlaybackRate) return;
        cancelAnimationFrame(rateRaf);
        const from = a.playbackRate;
        const t0 = performance.now();
        const step = (t) => {
          const k = clamp((t - t0) / 900, 0, 1);
          a.updatePlaybackRate(from + (to - from) * (1 - Math.pow(1 - k, 4)));
          if (k < 1) rateRaf = requestAnimationFrame(step);
        };
        rateRaf = requestAnimationFrame(step);
      };
      if (finePointer && el.dataset.hoverPause === 'true') {
        el.addEventListener('pointerenter', () => easeRate(0.0001));
        el.addEventListener('pointerleave', () => easeRate(1));
      }
      el.addEventListener('focusin', () => el.classList.add('is-held'));
      el.addEventListener('focusout', (e) => { if (!el.contains(e.relatedTarget)) el.classList.remove('is-held'); });
      const toggle = el.querySelector('[data-marquee-toggle]');
      if (toggle) toggle.addEventListener('click', () => {
        const paused = el.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      });
      if (hasIO) new IntersectionObserver(([e]) => el.classList.toggle('is-offscreen', !e.isIntersecting)).observe(el);
    });
  };

  /* Smooth inertia scroll — desktop wheel only, never in the editor */
  const initSmooth = () => {
    if (!on('moSmooth') || !finePointer || designMode || !ds.moLenisSrc) return;
    idle(() => {
      const s = document.createElement('script');
      s.src = ds.moLenisSrc;
      s.async = true;
      s.onload = () => {
        if (!window.Lenis) return;
        const lenis = new window.Lenis({ lerp: 0.085, wheelMultiplier: 0.95, smoothWheel: true });
        window.mymLenis = lenis;
        const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
        requestAnimationFrame(raf);
        // drawers lock the page with body overflow:hidden — pause Lenis with them
        new MutationObserver(() => {
          if (body.style.overflow === 'hidden') lenis.stop(); else lenis.start();
        }).observe(body, { attributes: true, attributeFilter: ['style'] });
      };
      document.head.appendChild(s);
    });
  };

  /* Cursor: a fine ring that trails the pointer; widens over links, opens with a label over imagery.
     The system cursor stays visible. Buttons and icons are gently magnetic. */
  const initCursor = () => {
    if (!on('moCursor') || !finePointer || designMode) return;
    const c = document.createElement('div');
    c.className = 'mo-cursor';
    c.setAttribute('aria-hidden', 'true');
    c.innerHTML = '<span class="mo-cursor__ring"></span><span class="mo-cursor__label"></span>';
    body.appendChild(c);
    const label = c.querySelector('.mo-cursor__label');
    const viewText = strings.cursorView || 'View';
    let x = -100, y = -100, cx = x, cy = y, raf = 0, shown = false;
    let magnet = null;
    const magnets = new Map();
    const reach = 7 * K;
    const loop = () => {
      cx += (x - cx) * 0.2;
      cy += (y - cy) * 0.2;
      c.style.translate = `${cx.toFixed(1)}px ${cy.toFixed(1)}px`;
      let busy = Math.abs(x - cx) > 0.2 || Math.abs(y - cy) > 0.2;
      magnets.forEach((m, el) => {
        m.x += (m.tx - m.x) * 0.12;
        m.y += (m.ty - m.y) * 0.12;
        el.style.translate = `${m.x.toFixed(2)}px ${m.y.toFixed(2)}px`;
        if (Math.abs(m.tx - m.x) > 0.05 || Math.abs(m.ty - m.y) > 0.05) busy = true;
        else if (el !== magnet) { el.style.translate = ''; magnets.delete(el); }
      });
      raf = busy ? requestAnimationFrame(loop) : 0;
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
    document.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      x = e.clientX; y = e.clientY;
      if (!shown) { shown = true; cx = x; cy = y; c.classList.add('is-visible'); }
      if (magnet) {
        const r = magnet.getBoundingClientRect();
        const m = magnets.get(magnet);
        m.tx = clamp((x - (r.left + r.width / 2)) * 0.25, -reach, reach);
        m.ty = clamp((y - (r.top + r.height / 2)) * 0.25, -reach, reach);
      }
      kick();
    }, { passive: true });
    document.addEventListener('pointerover', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const media = t.closest('.card__media, .look, .split__media, .collection-card__media, .article-card__media, .atelier__media a, [data-cursor-view]');
      const link = t.closest('a, button, summary, label, select, [role="button"]');
      c.classList.toggle('is-view', !!media);
      c.classList.toggle('is-link', !!link && !media);
      c.classList.toggle('is-field', !!t.closest('input, textarea, select, iframe, [contenteditable]'));
      label.textContent = media ? viewText : '';
      const m = t.closest('.button, .header__icon, .chip, .marquee__toggle, [data-magnetic]');
      if (m !== magnet) {
        if (magnet && magnets.has(magnet)) { const old = magnets.get(magnet); old.tx = 0; old.ty = 0; }
        magnet = m;
        if (m && !magnets.has(m)) magnets.set(m, { x: 0, y: 0, tx: 0, ty: 0 });
        kick();
      }
    }, { passive: true });
    document.addEventListener('pointerdown', () => c.classList.add('is-press'), { passive: true });
    document.addEventListener('pointerup', () => c.classList.remove('is-press'), { passive: true });
    root.addEventListener('pointerleave', () => c.classList.add('is-out'));
    root.addEventListener('pointerenter', () => c.classList.remove('is-out'));
  };

  /* Page transitions: native cross-document View Transitions where supported (CSS @view-transition),
     otherwise a short veil before navigating. */
  const initTransitions = () => {
    if (!on('moTransitions') || designMode || 'onpagereveal' in window) return;
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element && e.target.closest('a[href]');
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      if (/^\/(cart\/(add|change|clear)|account\/logout|checkout)/.test(url.pathname)) return;
      e.preventDefault();
      root.classList.add('mo-leaving');
      setTimeout(() => { window.location.href = url.href; }, 450);
      setTimeout(() => root.classList.remove('mo-leaving'), 4000);
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) root.classList.remove('mo-leaving'); });
  };

  const refresh = (scope = document) => {
    initMedia(scope);
    initSlideshows(scope, true);
    initHeroes(scope);
    initReveal(scope);
    initParallax(scope);
    initShimmer(scope);
    initMarquees(scope);
  };
  window.mymMotion = { refresh };

  refresh();
  initSmooth();
  initCursor();
  initTransitions();
  document.addEventListener('shopify:section:load', (e) => refresh(e.target));
})();
